import * as React from "react";
import { format } from "date-fns";
import { zhCN, enUS, ja } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const localeMap: Record<string, typeof zhCN> = {
  zh: zhCN,
  en: enUS,
  ja: ja,
  jp: ja,
};

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = localeMap[i18n.language] || enUS;
  const displayPlaceholder =
    placeholder || t("common.selectDate", "Select date");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-9 bg-background/50",
            !value && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? (
            format(value, "yyyy-MM-dd", { locale })
          ) : (
            <span>{displayPlaceholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          initialFocus
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
}
